import api from './client';

export interface Notification {
  id: string;
  type: 'breeding' | 'health' | 'task' | 'general' | 'supply_alert' | 'supply_expiry' | 
        'equipment_alert' | 'disease_alert' | 'financial_alert' | 'customer_notification' |
        'environmental_alert' | 'quarantine_alert' | 'vaccination_reminder' | 'maintenance_due' |
        'low_stock_warning' | 'critical_stock_alert' | 'expired_stock_alert' | 'document_expiry' |
        'performance_alert' | 'compliance_reminder' | 'system' | 'alert';
  subtype?: 'incubation' | 'hatching' | 'banding' | 'feeding' | 'checkup' | 'reminder';
  title: string;
  message: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | 'CRITICAL';
  status: 'UNREAD' | 'READ' | 'DISMISSED' | 'ARCHIVED' | 'ACTIONED';
  recipientId?: string;
  entityType?: 'breeding_pair' | 'bird' | 'species' | 'clutch' | 'egg' | 'cage' | 'task' | 'user' |
               'feed_stock' | 'supply' | 'supplier' | 'customer' | 'equipment' | 'health_test' |
               'disease_record' | 'document' | 'transaction' | 'account' | 'expense' | 'purchase' |
               'sale' | 'note' | 'photo' | 'system_setting';
  entityId?: string;
  metadata?: {
    moduleType?: string;
    eventType?: string;
    channels?: string[];
    escalationLevel?: string;
    actionUrl?: string;
    actionText?: string;
    icon?: string;
    tags?: string[];
    [key: string]: any;
  };
  scheduledFor?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  readAt?: string;
}

export interface CreateNotificationDto {
  type: Notification['type'];
  subtype?: Notification['subtype'];
  title: string;
  message: string;
  priority: Notification['priority'];
  recipientId?: string;
  entityType?: Notification['entityType'];
  entityId?: string;
  metadata?: Notification['metadata'];
  scheduledFor?: string;
  expiresAt?: string;
}

export interface BreedingNotificationData {
  breedingPairId: string;
  clutchId?: string;
  eggId?: string;
  maleBirdId: string;
  femaleBirdId: string;
  cageNumber: string;
  dueDate: string;
  daysRemaining: number;
}

export interface NotificationStats {
  total: number;
  unread: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
  byModule: Record<string, number>;
  trends: {
    daily: number[];
    weekly: number[];
    monthly: number[];
  };
}

export interface NotificationFilters {
  type?: string;
  status?: string;
  priority?: string;
  recipientId?: string;
  entityType?: string;
  entityId?: string;
  moduleType?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export const notificationsApi = {
  // General notification CRUD with enhanced filtering
  getAll: (params?: NotificationFilters) => {
    const queryParams: Record<string, string> = {};
    if (params?.type) queryParams.type = params.type;
    if (params?.status) queryParams.status = params.status;
    if (params?.priority) queryParams.priority = params.priority;
    if (params?.recipientId) queryParams.recipientId = params.recipientId;
    if (params?.entityType) queryParams.entityType = params.entityType;
    if (params?.entityId) queryParams.entityId = params.entityId;
    if (params?.moduleType) queryParams.moduleType = params.moduleType;
    if (params?.dateFrom) queryParams.dateFrom = params.dateFrom;
    if (params?.dateTo) queryParams.dateTo = params.dateTo;
    if (params?.limit) queryParams.limit = params.limit.toString();
    if (params?.offset) queryParams.offset = params.offset.toString();
    
    return api.get<Notification[]>('notifications', Object.keys(queryParams).length > 0 ? queryParams : undefined);
  },

  getById: (id: string) => api.get<Notification>(`notifications/${id}`),

  create: (data: CreateNotificationDto) => api.post<Notification>('notifications', data),

  update: (id: string, data: Partial<Notification>) => 
    api.patch<Notification>(`notifications/${id}`, data),

  delete: (id: string) => api.delete(`notifications/${id}`),

  markAsRead: (id: string) => 
    api.put<Notification>(`notifications/${id}/read`, {}),

  markAsUnread: (id: string) => 
    api.put<Notification>(`notifications/${id}/unread`, {}),

  dismiss: (id: string) => 
    api.put<Notification>(`notifications/${id}/dismiss`, {}),

  archive: (id: string) => 
    api.put<Notification>(`notifications/${id}/archive`, {}),

  markAsActioned: (id: string) => 
    api.put<Notification>(`notifications/${id}/actioned`, {}),

  // Bulk operations
  markAllAsRead: (recipientId?: string) => 
    api.put('notifications/mark-all-read', { recipientId }),

  bulkUpdate: (ids: string[], updates: Partial<Notification>) =>
    api.put('notifications/bulk-update', { ids, updates }),

  bulkDelete: (ids: string[]) =>
    api.post('notifications/bulk-delete', { ids }),

  getUnreadCount: (recipientId?: string) => 
    api.get<{ count: number }>('notifications/unread-count', recipientId ? { recipientId } : undefined),

  // Statistics and analytics
  getStats: (params?: { 
    recipientId?: string; 
    dateFrom?: string; 
    dateTo?: string;
    moduleType?: string;
  }) => api.get<NotificationStats>('notifications/stats', params),

  getTrends: (params?: {
    period: 'daily' | 'weekly' | 'monthly';
    limit?: number;
    moduleType?: string;
  }) => api.get('notifications/trends', params ? { ...params, limit: params.limit?.toString() } : undefined),

  // Entity-specific notifications
  getByEntity: (entityType: string, entityId: string) => 
    api.get<Notification[]>(`notifications/entity/${entityType}/${entityId}`),

  getByModule: (moduleType: string, params?: { limit?: number; status?: string }) =>
    api.get<Notification[]>(`notifications/module/${moduleType}`, params ? { ...params, limit: params.limit?.toString() } : undefined),

  // Priority-based queries
  getHighPriority: (recipientId?: string) =>
    api.get<Notification[]>('notifications/high-priority', recipientId ? { recipientId } : undefined),

  getCritical: (recipientId?: string) =>
    api.get<Notification[]>('notifications/critical', recipientId ? { recipientId } : undefined),

  getOverdue: (recipientId?: string) =>
    api.get<Notification[]>('notifications/overdue', recipientId ? { recipientId } : undefined),

  // Breeding-specific endpoints (enhanced)
  createBreedingNotification: (data: {
    type: 'incubation' | 'hatching' | 'banding';
    breedingData: BreedingNotificationData;
    customMessage?: string;
  }) => api.post<Notification>('notifications/breeding', data),

  getBreedingNotifications: (breedingPairId?: string) => 
    api.get<Notification[]>('notifications/breeding', breedingPairId ? { breedingPairId } : undefined),

  updateBreedingSchedule: (breedingPairId: string, clutchId: string) =>
    api.post<Notification[]>(`notifications/breeding/${breedingPairId}/clutches/${clutchId}/schedule`, {}),

  // Health-specific endpoints
  getHealthNotifications: (birdId?: string) =>
    api.get<Notification[]>('notifications/health', birdId ? { birdId } : undefined),

  createHealthAlert: (data: {
    birdId: string;
    alertType: 'test_overdue' | 'vaccination_due' | 'disease_detected';
    severity: 'low' | 'medium' | 'high' | 'critical';
    details: any;
  }) => api.post<Notification>('notifications/health/alert', data),

  // Supply management endpoints
  getSupplyNotifications: () =>
    api.get<Notification[]>('notifications/supply'),

  createSupplyAlert: (data: {
    supplyId: string;
    alertType: 'low_stock' | 'critical_stock' | 'expired';
    currentLevel: number;
    threshold: number;
  }) => api.post<Notification>('notifications/supply/alert', data),

  // Equipment endpoints
  getEquipmentNotifications: () =>
    api.get<Notification[]>('notifications/equipment'),

  createEquipmentAlert: (data: {
    equipmentId: string;
    alertType: 'maintenance_due' | 'failure' | 'calibration_due';
    severity: 'low' | 'medium' | 'high' | 'critical';
    details: any;
  }) => api.post<Notification>('notifications/equipment/alert', data),

  // Customer service endpoints
  getCustomerNotifications: (customerId?: string) =>
    api.get<Notification[]>('notifications/customer', customerId ? { customerId } : undefined),

  createCustomerNotification: (data: {
    customerId: string;
    type: 'follow_up' | 'complaint' | 'order_update';
    message: string;
    priority: Notification['priority'];
  }) => api.post<Notification>('notifications/customer', data),

  // Financial alerts
  getFinancialNotifications: () =>
    api.get<Notification[]>('notifications/financial'),

  createFinancialAlert: (data: {
    alertType: 'budget_exceeded' | 'expense_approval' | 'payment_due';
    amount: number;
    entityId: string;
    entityType: string;
  }) => api.post<Notification>('notifications/financial/alert', data),

  // Document management
  getDocumentNotifications: () =>
    api.get<Notification[]>('notifications/documents'),

  createDocumentAlert: (data: {
    documentId: string;
    alertType: 'expiring' | 'expired' | 'renewal_due';
    expiryDate: string;
    daysUntilExpiry: number;
  }) => api.post<Notification>('notifications/documents/alert', data),

  // System notifications
  getSystemNotifications: () =>
    api.get<Notification[]>('notifications/system'),

  createSystemAlert: (data: {
    alertType: 'performance' | 'error' | 'maintenance';
    severity: 'low' | 'medium' | 'high' | 'critical';
    message: string;
    details?: any;
  }) => api.post<Notification>('notifications/system/alert', data),

  // Notification preferences
  getPreferences: (userId?: string) => 
    api.get('notifications/preferences', userId ? { userId } : undefined),

  updatePreferences: (preferences: {
    email?: boolean;
    push?: boolean;
    inApp?: boolean;
    sms?: boolean;
    webhook?: boolean;
    types?: string[];
    priorities?: string[];
    modules?: string[];
    quietHours?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    escalationSettings?: {
      enabled: boolean;
      levels: string[];
      delays: number[];
    };
  }) => api.put('notifications/preferences', preferences),

  // Advanced features
  snooze: (id: string, snoozeUntil: string) =>
    api.put(`notifications/${id}/snooze`, { snoozeUntil }),

  escalate: (id: string, escalationLevel: string) =>
    api.put(`notifications/${id}/escalate`, { escalationLevel }),

  createTemplate: (template: {
    name: string;
    moduleType: string;
    eventType: string;
    title: string;
    message: string;
    priority: Notification['priority'];
  }) => api.post('notifications/templates', template),

  getTemplates: (moduleType?: string) =>
    api.get('notifications/templates', moduleType ? { moduleType } : undefined),

  // Real-time features
  subscribe: (recipientId: string) =>
    api.get(`notifications/subscribe/${recipientId}`),

  // Export and reporting
  export: (params: NotificationFilters & { format: 'csv' | 'xlsx' | 'pdf' }) =>
    api.get('notifications/export', params as any),

  generateReport: (params: {
    type: 'summary' | 'detailed' | 'trends';
    period: 'daily' | 'weekly' | 'monthly';
    moduleTypes?: string[];
    dateFrom: string;
    dateTo: string;
  }) => api.post('notifications/reports', params)
};

// Utility functions for breeding notifications (enhanced)
export const createBreedingNotifications = {
  incubationDue: (data: BreedingNotificationData): CreateNotificationDto => ({
    type: 'breeding',
    subtype: 'incubation',
    title: 'Incubation Due',
    message: `Eggs in cage ${data.cageNumber} should begin incubation ${data.daysRemaining <= 0 ? 'today' : `in ${data.daysRemaining} days`}`,
    priority: data.daysRemaining <= 0 ? 'HIGH' : data.daysRemaining <= 2 ? 'MEDIUM' : 'LOW',
    entityType: 'breeding_pair',
    entityId: data.breedingPairId,
    metadata: {
      moduleType: 'breeding',
      eventType: 'incubation.due',
      breedingPairId: data.breedingPairId,
      clutchId: data.clutchId,
      eggId: data.eggId,
      cageNumber: data.cageNumber,
      dueDate: data.dueDate,
      daysRemaining: data.daysRemaining,
      actionUrl: `/breeding/pairs/${data.breedingPairId}`,
      actionText: 'View Breeding Pair'
    },
    scheduledFor: data.dueDate,
  }),

  hatchingDue: (data: BreedingNotificationData): CreateNotificationDto => ({
    type: 'breeding',
    subtype: 'hatching',
    title: 'Hatching Expected',
    message: `Eggs in cage ${data.cageNumber} are expected to hatch ${data.daysRemaining <= 0 ? 'today' : `in ${data.daysRemaining} days`}`,
    priority: data.daysRemaining <= 0 ? 'URGENT' : data.daysRemaining <= 3 ? 'HIGH' : 'MEDIUM',
    entityType: 'breeding_pair',
    entityId: data.breedingPairId,
    metadata: {
      moduleType: 'breeding',
      eventType: 'hatching.due',
      breedingPairId: data.breedingPairId,
      clutchId: data.clutchId,
      eggId: data.eggId,
      cageNumber: data.cageNumber,
      dueDate: data.dueDate,
      daysRemaining: data.daysRemaining,
      actionUrl: `/breeding/pairs/${data.breedingPairId}`,
      actionText: 'Check Hatching Progress'
    },
    scheduledFor: data.dueDate,
  }),

  bandingDue: (data: BreedingNotificationData): CreateNotificationDto => ({
    type: 'breeding',
    subtype: 'banding',
    title: 'Banding Required',
    message: `Chicks in cage ${data.cageNumber} should be banded ${data.daysRemaining <= 0 ? 'today' : `in ${data.daysRemaining} days`}`,
    priority: data.daysRemaining <= 0 ? 'URGENT' : data.daysRemaining <= 2 ? 'HIGH' : 'MEDIUM',
    entityType: 'breeding_pair',
    entityId: data.breedingPairId,
    metadata: {
      moduleType: 'breeding',
      eventType: 'banding.due',
      breedingPairId: data.breedingPairId,
      clutchId: data.clutchId,
      eggId: data.eggId,
      cageNumber: data.cageNumber,
      dueDate: data.dueDate,
      daysRemaining: data.daysRemaining,
      actionUrl: `/birds/banding`,
      actionText: 'Manage Banding'
    },
    scheduledFor: data.dueDate,
  }),
};

export default notificationsApi; 